   import React, { useState, useEffect, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Plus, Search, Calendar as CalendarIcon } from 'lucide-react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { toast } from 'sonner';
import TaskTableView from '../components/ui/TaskTableView';
import CalendarView from '../components/CalendarView';
import { FiGrid, FiList } from 'react-icons/fi';
import ProblemCard from '../components/ProblemCard';
import ProblemDetailModal from '../components/ProblemDetailModal';
import ProblemFormModal from '../components/ProblemFormModal';
import CustomDropdown from '../components/ui/CustomDropdown';
import BadgeDropdown from '../components/ui/BadgeDropdown';
import { fetchProblems, createProblem, updateProblem, deleteProblem, saveSolutions, saveSubtasks, debugSolutionsRLS } from '../utils/supabaseHelpers';
import { supabase } from '../supabaseClient';

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [problems, setProblems] = useState([]);
  const [filteredProblems, setFilteredProblems] = useState([]);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingProblem, setEditingProblem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    // Load view mode from localStorage on initial render
    return localStorage.getItem('dashboardViewMode') || 'cards';
  }); // 'cards', 'table', or 'calendar'
  const [filters, setFilters] = useState({
    domain: 'all',
    status: 'all',
    priority: 'all'
  });
  const [filterAssignee, setFilterAssignee] = useState('All Users');
  const [users, setUsers] = useState([]);

  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('dashboardViewMode', viewMode);
  }, [viewMode]);

  // -- Add these lines --
  const sortOptions = [
    { id: 'created_at-desc', name: 'Newest', value: 'Sort: Newest' },
    { id: 'created_at-asc', name: 'Oldest', value: 'Sort: Oldest' },
    { id: 'updated_at-desc', name: 'Updated', value: 'Sort: Recently Updated' },
    { id: 'title-asc', name: 'Title A-Z', value: 'Sort: Title A-Z' },
    { id: 'title-desc', name: 'Title Z-A', value: 'Sort: Title Z-A' },
    { id: 'dueDate', name: 'Due Date', value: 'dueDate' },
  ];
  const [selectedSort, setSelectedSort] = useState(sortOptions[0]);
  // -- End of new lines --

  useEffect(() => {
    loadProblems();
    loadUsers();
  }, []);

  // Handle incoming filters from navigation (e.g., from Overview page)
  useEffect(() => {
    if (location.state?.filter) {
      const incomingFilter = location.state.filter;
      console.log('Dashboard: Received filter from navigation:', incomingFilter);
      
      // Map incoming filter to Dashboard's filter format
      if (incomingFilter.status) {
        let mappedStatus = 'all';
        
        // Map the status values from Overview format to Dashboard format
        if (incomingFilter.status === 'Done') {
          mappedStatus = 'done';
        } else if (incomingFilter.status === 'In Progress') {
          mappedStatus = 'in_progress';
        } else if (incomingFilter.status === 'Hold') {
          mappedStatus = 'hold';
        } else if (incomingFilter.status === 'Not Decided') {
          mappedStatus = 'not_decided';
        }
        
        setFilters(prev => ({ ...prev, status: mappedStatus }));
        console.log('Dashboard: Applied status filter:', mappedStatus);
      }
      
      // Handle priority filter
      if (incomingFilter.priority) {
        setFilters(prev => ({ ...prev, priority: incomingFilter.priority.toLowerCase() }));
        console.log('Dashboard: Applied priority filter:', incomingFilter.priority);
      }
      
      // Clear the state after applying to prevent re-applying on re-renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Handle navigation from Assignments page to auto-open problem modal
  useEffect(() => {
    const problemToOpenId = location.state?.openProblemId;
    if (problemToOpenId) {
      // 1. Find the problem from the full list
      const problemToOpen = problems.find(p => p.id === problemToOpenId);
      if (problemToOpen) {
        // 2. Open the problem detail modal
        setSelectedProblem(problemToOpen);
        setShowDetailModal(true);

        // 3. Clear the state to prevent re-opening
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, problems, navigate]);

  const loadUsers = async () => {
    try {
      // Check if user is authenticated
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('Authentication error:', authError);
        setUsers([]);
        return;
      }

      if (!user) {
        console.log('No authenticated user found');
        setUsers([]);
        return;
      }

      console.log('Authenticated user ID:', user.id);

      // Fetch users from the database - using * to get all columns
      const { data, error, count } = await supabase
        .from('users')
        .select('*', { count: 'exact' })
        .order('email', { ascending: true });

      console.log('Users query result:', { 
        data, 
        error, 
        count,
        dataLength: data?.length 
      });

      if (error) {
        console.error('Error fetching users from database:', error.message, error);
        toast.error('Unable to load users list');
        setUsers([]);
        return;
      }

      if (!data || data.length === 0) {
        console.warn('No users found in database');
        toast.info('No users found in system');
        setUsers([]);
        return;
      }

      console.log('Successfully loaded users:', data.length);
      // Map users to include both id and name (using email as fallback)
      const mappedUsers = data.map(u => ({
        id: u.id,
        full_name: u.name || u.username || u.email || u.id,
        is_active: u.is_active,
        inactive_user_name: u.inactive_user_name
      }));
      setUsers(mappedUsers);
    } catch (error) {
      console.error('Unexpected error loading users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    }
  };

  const loadProblems = async (sortOption = null) => {
    try {
      setLoading(true);
      const sortToUse = sortOption || selectedSort.value;
      // For 'dueDate', use default database sort (we'll sort in frontend)
      const dbSortOption = sortToUse === 'dueDate' ? 'Sort: Newest' : sortToUse;
      const data = await fetchProblems(dbSortOption);

      // Flatten data to include subtasks as rows
      const flatList = [];
      data.forEach(p => {
        // Add Main Task
        flatList.push({ ...p, type: 'problem' });

        // Add Subtasks
        if (p.subtasks && p.subtasks.length > 0) {
          p.subtasks.forEach(st => {
            flatList.push({
              id: st.id,
              title: `@ ${p.title} - ${st.title}`,
              description: `Subtask of ${p.title}`, // Placeholder description
              status: st.is_completed ? 'Done' : 'In Progress',
              domain: p.domain,
              priority: p.priority,
              due_date: st.due_date,
              assigned_to: st.assigned_to,
              assignee_name: (() => {
                const isInactive = st.assignee?.is_active === false;
                return isInactive && st.assignee?.inactive_user_name 
                  ? st.assignee.inactive_user_name 
                  : st.assignee?.name;
              })(),
              type: 'subtask',
              parentId: p.id,
              original_subtask: st
            });
          });
        }
      });

      setProblems(flatList);
    } catch (error) {
      toast.error('Failed to load problems: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to refresh a specific problem with solutions
  const refreshProblemWithSolutions = async (problemId) => {
    try {
      const refreshedProblems = await fetchProblems(selectedSort.value);
      const refreshedProblem = refreshedProblems.find(p => p.id === problemId);
      if (refreshedProblem && selectedProblem?.id === problemId) {
        setSelectedProblem(refreshedProblem);
        console.log('✅ Dashboard: Selected problem refreshed with solutions:', refreshedProblem.solutions?.length || 0);
      }
      return refreshedProblem;
    } catch (error) {
      console.error('❌ Dashboard: Error refreshing problem:', error);
      return null;
    }
  };

  // Reload data when sort changes
  useEffect(() => {
    if (selectedSort) {
      loadProblems(selectedSort.value);
    }
  }, [selectedSort]);

  // Helper function to map simplified status categories to database values
  const getStatusMapping = (category) => {
    switch (category) {
      case 'done':
        return ['Done', 'Solved', 'completed'];
      case 'in_progress':
        return ['In Progress', 'pending'];
      case 'hold':
        return ['Hold'];
      case 'not_decided':
        // This is the default case - includes all other statuses
        return ['Not Decided', 'decided_', 'new', 'under_discussion', 'solution_decided'];
      default:
        return [];
    }
  };

  useEffect(() => {
    let filtered = problems;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Domain filter
    if (filters.domain !== 'all') {
      filtered = filtered.filter(p => p.domain?.toLowerCase() === filters.domain.toLowerCase());
    }

    // Status filter - now uses simplified categories with mapping
    if (filters.status !== 'all') {
      const statusValues = getStatusMapping(filters.status);
      filtered = filtered.filter(p => {
        if (filters.status === 'not_decided') {
          // For "not_decided", include anything that's NOT in the other categories
          const doneStatuses = getStatusMapping('done');
          const inProgressStatuses = getStatusMapping('in_progress');
          const holdStatuses = getStatusMapping('hold');
          const excludedStatuses = [...doneStatuses, ...inProgressStatuses, ...holdStatuses];
          return !excludedStatuses.includes(p.status);
        } else {
          // For other categories, check if status is in the mapped values
          return statusValues.includes(p.status);
        }
      });
    }

    // Priority filter
    if (filters.priority !== 'all') {
      filtered = filtered.filter(p => p.priority?.toLowerCase() === filters.priority.toLowerCase());
    }

    // Assignee filter
    if (filterAssignee !== 'All Users') {
      filtered = filtered.filter(p => {
        const assigneeName = p.assignee_name || p.assignee?.name || '';
        return assigneeName === filterAssignee || 
               assigneeName.toLowerCase() === filterAssignee.toLowerCase();
      });
    }

    // Frontend sorting for Due Date (special handling for NULL values)
    if (selectedSort.value === 'dueDate') {
      filtered = [...filtered].sort((a, b) => {
        // If 'a' has no date, push it to the bottom (return 1)
        if (!a.due_date) return 1;
        // If 'b' has no date, push it to the bottom (return -1)
        if (!b.due_date) return -1;

        // Compare dates: Earliest date comes first (Ascending)
        return new Date(a.due_date) - new Date(b.due_date);
      });
    }
    // Other sorting is handled at the database level

    setFilteredProblems(filtered);
  }, [problems, searchTerm, filters, filterAssignee, selectedSort]);

  const handleCreateProblem = async (problemData) => {
    try {
      console.log('=== DASHBOARD CREATE STARTED ===');
      console.log('➕ Dashboard: Creating new problem with data:', problemData);
      console.log('➕ Dashboard: Solutions to save:', problemData.solutions);
      console.log('➕ Dashboard: Solutions count:', problemData.solutions?.length || 0);

      // Step 1: Create the problem record
      console.log('📝 Dashboard: Creating problem record...');
      const createData = {
        title: problemData.title,
        description: problemData.description,
        domain: problemData.domain,
        priority: problemData.priority,
        status: problemData.status,
        due_date: problemData.dueDate || null,
        notes: problemData.notes || '',
        project_id: problemData.project_id || null
        // Removed solutions - it's a separate table, not a column
      };

      // Include done_date if status is 'Done'
      if (problemData.status === 'Done' && problemData.done_date) {
        createData.done_date = problemData.done_date;
      }

      const newProblem = await createProblem(createData);

      console.log('✅ Dashboard: Problem created successfully:', newProblem);
      console.log('✅ Dashboard: New problem ID:', newProblem.id);

      // Step 2: Save solutions to separate table (with error handling)
      if (problemData.solutions && problemData.solutions.length > 0) {
        console.log('Dashboard: About to call saveSolutions for new problem...');
        console.log('Dashboard: Problem ID:', newProblem.id);
        console.log('Dashboard: Solutions data:', problemData.solutions);

        try {
          const result = await saveSolutions(newProblem.id, problemData.solutions);
          console.log('Dashboard: saveSolutions completed for new problem, returned:', result);
          console.log('Dashboard: Number of solutions saved:', result?.data?.length || 0);
        } catch (solutionsError) {
          console.error('Dashboard: Error saving solutions:', solutionsError);
          console.error('Dashboard: This is a blocking error - solutions must be saved properly');
          throw solutionsError; // Re-throw to prevent incomplete saves
        }
      } else {
        console.log('Dashboard: No solutions to save for new problem');
      }

      // Step 3: Save subtasks to separate table (with error handling)
      if (problemData.subtasks && problemData.subtasks.length > 0) {
        console.log('Dashboard: About to call saveSubtasks for new problem...');
        console.log('Dashboard: Problem ID:', newProblem.id);
        console.log('Dashboard: Subtasks data:', problemData.subtasks);

        try {
          const result = await saveSubtasks(newProblem.id, problemData.subtasks);
          console.log('Dashboard: saveSubtasks completed for new problem, returned:', result);
          console.log('Dashboard: Number of subtasks saved:', result?.data?.length || 0);
        } catch (subtasksError) {
          console.error('Dashboard: Error saving subtasks:', subtasksError);
          console.error('Dashboard: This is a blocking error - subtasks must be saved properly');
          throw subtasksError;
        }
      } else {
        console.log('Dashboard: No subtasks to save for new problem');
      }

      // Step 4: Refetch all problems to get the new problem with solutions and subtasks
      console.log('Dashboard: Refetching problems list to include new problem with solutions and subtasks...');
      await loadProblems(selectedSort.value);

      toast.success('Problem created successfully!');
      setShowFormModal(false);

      console.log('=== DASHBOARD CREATE COMPLETED SUCCESSFULLY ===');
    } catch (error) {
      console.error('=== DASHBOARD CREATE ERROR ===');
      console.error('❌ Dashboard: Failed to create problem:', error);
      console.error('❌ Dashboard: Error details:', {
        message: error.message,
        stack: error.stack,
        solutionsCount: problemData?.solutions?.length
      });

      // If it's a solutions-related error, run RLS debugging
      if (error.message?.includes('solutions') || error.message?.includes('delete') || error.message?.includes('RLS') || error.message?.includes('policy')) {
        console.log('🔍 Running RLS debugging due to solutions error...');
        await debugSolutionsRLS();
      }

      // If it's a function not found error, provide guidance
      if (error.message?.includes('function') && error.message?.includes('does not exist')) {
        console.error('🚨 SETUP REQUIRED: Please create the delete function in Supabase');
        console.error('🚨 Run the SQL commands in debug-rls-policies.sql in your Supabase SQL Editor');
        toast.error('Database function missing. Please check console for setup instructions.');
      } else {
        toast.error('Failed to create problem: ' + error.message);
      }
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      const { error } = await updateProblem(taskId, {
        status: newStatus
      });

      if (error) {
        toast.error('Failed to update status: ' + error.message);
        return;
      }

      // Refresh the problems list to show updated status
      await loadProblems(selectedSort.value);
      toast.success('Status updated successfully!');
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update status: ' + error.message);
    }
  };

  const handleUpdateProblem = async (problemData) => {
    try {
      console.log('=== DASHBOARD UPDATE STARTED ===');
      console.log('🔄 Dashboard: Starting problem update for ID:', problemData.id);
      console.log('🔄 Dashboard: Full problem data received:', problemData);
      console.log('🔄 Dashboard: Solutions to save:', problemData.solutions);
      console.log('🔄 Dashboard: Solutions count:', problemData.solutions?.length || 0);

      // Step 1: Update the problem record
      console.log('📝 Dashboard: Updating problem record...');
      const updateData = {
        title: problemData.title,
        description: problemData.description,
        domain: problemData.domain,
        priority: problemData.priority,
        status: problemData.status,
        due_date: problemData.dueDate || null,
        notes: problemData.notes || '',
        project_id: problemData.project_id || null
        // Removed solutions - it's a separate table, not a column
      };

      // Include done_date if status is 'Done'
      if (problemData.status === 'Done' && problemData.done_date) {
        updateData.done_date = problemData.done_date;
      } else if (problemData.status !== 'Done') {
        // Clear done_date if status is not 'Done'
        updateData.done_date = null;
      }

      const updatedProblem = await updateProblem(problemData.id, updateData);

      console.log('✅ Dashboard: Problem updated successfully:', updatedProblem);

      // Step 2: Save solutions to separate table (with error handling)
      console.log('Dashboard: About to call saveSolutions...');
      console.log('Dashboard: Problem ID:', problemData.id);
      console.log('Dashboard: Solutions data:', problemData.solutions || []);

      try {
        const result = await saveSolutions(problemData.id, problemData.solutions || []);
        console.log('Dashboard: saveSolutions completed, returned:', result);
        console.log('Dashboard: Number of solutions returned:', result?.data?.length || 0);
      } catch (solutionsError) {
        console.error('Dashboard: Error saving solutions:', solutionsError);
        console.error('Dashboard: This is a blocking error - solutions must be saved properly');
        throw solutionsError; // Re-throw to prevent incomplete saves
      }

      // Step 3: Save subtasks to separate table (with error handling)
      console.log('Dashboard: About to call saveSubtasks...');
      console.log('Dashboard: Problem ID:', problemData.id);
      console.log('Dashboard: Subtasks data:', problemData.subtasks || []);

      try {
        const result = await saveSubtasks(problemData.id, problemData.subtasks || []);
        console.log('Dashboard: saveSubtasks completed, returned:', result);
        console.log('Dashboard: Number of subtasks returned:', result?.data?.length || 0);
      } catch (subtasksError) {
        console.error('Dashboard: Error saving subtasks:', subtasksError);
        console.error('Dashboard: This is a blocking error - subtasks must be saved properly');
        throw subtasksError;
      }

      // Step 4: Refetch all problems to get updated solutions and subtasks
      console.log('Dashboard: Refetching problems list to get updated solutions and subtasks...');
      await loadProblems(selectedSort.value);

      // Step 5: Update selected problem if it's the one we just updated
      if (selectedProblem?.id === updatedProblem.id) {
        await refreshProblemWithSolutions(updatedProblem.id);
      }

      toast.success('Problem updated successfully!');
      setShowFormModal(false);
      setEditingProblem(null);

      console.log('=== DASHBOARD UPDATE COMPLETED SUCCESSFULLY ===');
    } catch (error) {
      console.error('=== DASHBOARD UPDATE ERROR ===');
      console.error('❌ Dashboard: Failed to update problem:', error);
      console.error('❌ Dashboard: Error details:', {
        message: error.message,
        stack: error.stack,
        problemId: problemData?.id,
        solutionsCount: problemData?.solutions?.length
      });

      // If it's a solutions-related error, run RLS debugging
      if (error.message?.includes('solutions') || error.message?.includes('delete') || error.message?.includes('RLS') || error.message?.includes('policy')) {
        console.log('🔍 Running RLS debugging due to solutions error...');
        await debugSolutionsRLS();
      }

      // If it's a function not found error, provide guidance
      if (error.message?.includes('function') && error.message?.includes('does not exist')) {
        console.error('🚨 SETUP REQUIRED: Please create the delete function in Supabase');
        console.error('🚨 Run the SQL commands in debug-rls-policies.sql in your Supabase SQL Editor');
        toast.error('Database function missing. Please check console for setup instructions.');
      } else {
        toast.error('Failed to update problem: ' + error.message);
      }
    }
  };

  const handleDeleteProblem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this problem?')) return;

    try {
      await deleteProblem(id);
      setProblems(problems.filter(p => p.id !== id));
      toast.success('Problem deleted successfully');
      setShowDetailModal(false);
      setSelectedProblem(null);
    } catch (error) {
      toast.error('Failed to delete problem: ' + error.message);
    }
  };

  const handleEditClick = (problem) => {
    setEditingProblem(problem);
    setShowFormModal(true);
    setShowDetailModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-400">Loading problems...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 overflow-visible" data-testid="dashboard-page">
      {/* Search & Filters */}
      <div className="glass-card p-3 mb-8 relative z-40 overflow-visible">
        <div className="flex flex-col gap-3 overflow-visible">
          {/* Top Row: Search and View Toggle */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="flex-1 relative min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search entries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full glass-input text-sm py-2"
                style={{ paddingLeft: '40px' }}
                data-testid="search-input"
              />
            </div>

            {/* View Toggle Buttons */}
            <div className="flex p-1 bg-gray-700 rounded-md">
              <button
                onClick={() => setViewMode('cards')}
                className={`flex items-center px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${viewMode === 'cards'
                  ? 'bg-indigo-600 text-white shadow'
                  : 'text-gray-300 hover:text-white'
                  }`}
              >
                <FiGrid className="inline-block mr-1.5 w-3.5 h-3.5" />
                Cards
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${viewMode === 'table'
                  ? 'bg-indigo-600 text-white shadow'
                  : 'text-gray-300 hover:text-white'
                  }`}
              >
                <FiList className="inline-block mr-1.5 w-3.5 h-3.5" />
                Table
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${viewMode === 'calendar'
                  ? 'bg-indigo-600 text-white shadow'
                  : 'text-gray-300 hover:text-white'
                  }`}
              >
                <CalendarIcon className="inline-block mr-1.5" size={14} />
                Calendar
              </button>
            </div>

            {/* Create Task Button */}
            <button
              onClick={() => {
                setEditingProblem(null);
                setShowFormModal(true);
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap"
              data-testid="create-problem-btn"
            >
              <Plus size={16} />
              Create Task
            </button>
          </div>

          {/* Bottom Row: Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <BadgeDropdown
              value={filters.domain}
              onChange={(value) => setFilters({ ...filters, domain: value })}
              type="domain"
              showAllOption={true}
              options={[
                { value: 'mechanical', label: 'Mechanical' },
                { value: 'software', label: 'Software' },
                { value: 'electronics', label: 'Electronics' },
                { value: 'operations', label: 'Operations' }
              ]}
              className="text-xs z-50"
              data-testid="filter-domain"
            />

            <BadgeDropdown
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              type="status"
              showAllOption={true}
              options={[
                { value: 'not_decided', label: 'Not Decided' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'hold', label: 'Hold' },
                { value: 'done', label: 'Done' }
              ]}
              className="text-xs z-40"
              data-testid="filter-status"
            />

            <BadgeDropdown
              value={filters.priority}
              onChange={(value) => setFilters({ ...filters, priority: value })}
              type="priority"
              showAllOption={true}
              options={[
                { value: 'critical', label: 'Critical' },
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' }
              ]}
              className="text-xs z-30"
              data-testid="filter-priority"
            />

            {/* Assignee Filter */}
            {(() => {
              // Build assignee options from users table
              const userOptions = users && users.length > 0 
                ? users.map(user => {
                    const isInactive = user.is_active === false;
                    const realName = user.full_name || user.id;
                    const displayName = isInactive && user.inactive_user_name 
                      ? user.inactive_user_name 
                      : realName;
                    return {
                      value: realName, // Keep real name for filtering
                      label: displayName,
                      isInactive: isInactive
                    };
                  })
                : [];

              const assigneeOptions = [
                { value: 'All Users', label: 'All Users' },
                ...userOptions
              ];

              return (
                <CustomDropdown
                  value={filterAssignee}
                  onChange={(value) => setFilterAssignee(value)}
                  options={assigneeOptions}
                  placeholder="Assignee"
                  className="text-xs z-20"
                  renderOption={(option) => (
                    <span className={option.isInactive ? 'text-gray-500' : ''}>
                      {option.label}
                    </span>
                  )}
                />
              );
            })()}

            {/* Sort By Dropdown */}
            <div className="relative z-10">
              <Listbox value={selectedSort} onChange={setSelectedSort}>
                <div className="relative">
                  <Listbox.Button className="glass-input cursor-pointer text-left pl-3 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all duration-200 hover:border-indigo-400/50 text-xs whitespace-nowrap">
                    <span className="block truncate">{selectedSort.name}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Listbox.Options
                      className="absolute mt-1 min-w-full w-max max-w-[300px] overflow-visible rounded-xl bg-gray-900/95 backdrop-blur-xl border border-white/10 shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-1"
                      style={{ zIndex: 9999 }}
                    >
                      {sortOptions.map((option) => (
                        <Listbox.Option
                          key={option.id}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2.5 pl-10 pr-4 transition-all duration-200 ${active ? 'bg-white/10 text-white' : 'text-gray-300 hover:bg-white/5'
                            }`
                          }
                          value={option}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block whitespace-nowrap text-sm ${selected ? 'font-medium text-white' : 'font-normal'
                                  }`}
                              >
                                {option.name}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-400">
                                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>
          </div>
        </div>
      </div>

      {/* === Conditional View === */}
      {viewMode === 'calendar' ? (
        // --- CALENDAR VIEW ---
        <CalendarView
          problems={filteredProblems}
          onTaskClick={(problem) => {
            setSelectedProblem(problem);
            setShowDetailModal(true);
          }}
        />
      ) : viewMode === 'cards' ? (
        // --- CARD VIEW ---
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
          {filteredProblems
            .filter(p => p.type !== 'subtask') // Filter out subtasks for Card View
            .map((problem) => (
              <ProblemCard
                key={problem.id}
                problem={problem}
                onClick={() => {
                  setSelectedProblem(problem);
                  setShowDetailModal(true);
                }}
              />
            ))}
        </div>
      ) : (
        // --- TABLE VIEW ---
        <TaskTableView problems={filteredProblems} onRefresh={loadProblems} onUpdateTask={handleUpdateTaskStatus} />
      )}
      {/* === End Conditional View === */}

      {filteredProblems.length === 0 && !loading && (
        <div className="glass-card p-12 text-center" data-testid="no-problems">
          <AlertCircle className="mx-auto mb-4 text-gray-400" size={48} />
          <p className="text-gray-400 text-lg mb-4">
            {problems.length === 0 ? 'No problems yet. Create your first problem!' : 'No problems found matching your criteria'}
          </p>
          {problems.length === 0 && (
            <button
              onClick={() => setShowFormModal(true)}
              className="btn-primary"
            >
              Create Task
            </button>
          )}
        </div>
      )}

      {filteredProblems.length > 0 && (
        <div className="text-center text-gray-400" data-testid="problem-count">
          Showing {filteredProblems.length} of {problems.length} problems
        </div>
      )}

      {/* Modals */}
      {showDetailModal && selectedProblem && (
        <ProblemDetailModal
          problem={selectedProblem}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedProblem(null);
          }}
          onEdit={() => handleEditClick(selectedProblem)}
          onDelete={() => handleDeleteProblem(selectedProblem.id)}
          onUpdate={async (updatedProblem) => {
            try {
              const updateData = {
                status: updatedProblem.status
              };

              // Include done_date if status is 'Done'
              if (updatedProblem.status === 'Done' && updatedProblem.done_date) {
                updateData.done_date = updatedProblem.done_date;
              } else if (updatedProblem.status !== 'Done') {
                // Clear done_date if status is not 'Done'
                updateData.done_date = null;
              }

              const updated = await updateProblem(updatedProblem.id, updateData);

              // Refetch all problems to ensure we have the latest data with solutions
              console.log('🔄 Dashboard: Refetching problems after status update...');
              await loadProblems(selectedSort.value);

              // Update selected problem with fresh data including solutions
              await refreshProblemWithSolutions(updated.id);

              toast.success('Status updated');
            } catch (error) {
              toast.error('Failed to update: ' + error.message);
            }
          }}
        />
      )}

      {showFormModal && (
        <ProblemFormModal
          problem={editingProblem}
          onClose={() => {
            setShowFormModal(false);
            setEditingProblem(null);
          }}
          onSubmit={editingProblem ? handleUpdateProblem : handleCreateProblem}
        />
      )}
    </div>
  );
};

export default Dashboard;
